import React, { useContext, useEffect } from 'react';
import { renderRoutes } from 'react-router-config';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import { paths } from '../../../paths';
import { OPEN_SESSION } from '../../../graphql/queries';
import { useQuery, useRouter, useNotification, useBasketCookie } from '../../../hooks';
import ErrorDisplay from '../ErrorDisplay';
import { LocalizedRedirect } from '../../../components/Routing';
import { PurchaseContext } from '../../../store/Purchase';

const BuyingProcessLayout = ({ route: { routes } }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { newNotification } = useNotification();
  const {
    generatePath,
    currentRoute: { search, params },
  } = useRouter();
  const { getPromoCodeOrVoucher } = useBasketCookie();
  const promoCodeOrVoucher = getPromoCodeOrVoucher();

  const { sessionId, registrationId, locale, country } = params;

  const errorDisplay = (translation) => {
    return (
      <ErrorDisplay
        errorTitle={t(translation)}
        hasSession={!!sessionId}
        link={sessionId ? paths.SESSION_SEARCH : paths.PREP_TOOLS}
      />
    );
  };

  const {
    data: { openSession, ...productsAndServices },
    loading: sessionLoading,
  } = useQuery(OPEN_SESSION, {
    variables: {
      sessionId,
      lang: locale,
      country,
      discount:
        promoCodeOrVoucher?.discountCode && !promoCodeOrVoucher?.isVoucher
          ? { discountCode: promoCodeOrVoucher.discountCode, referrer: promoCodeOrVoucher.referrer }
          : null,
    },
    skip: !sessionId,
  });

  const formatProductsAnsServices = (productsAndServices) => {
    if (Object.keys(productsAndServices).length === 0 || !promoCodeOrVoucher?.productAndServiceIds) {
      return productsAndServices;
    }

    for (let type in productsAndServices) {
      productsAndServices[type] = productsAndServices[type].map((ps) => ({
        ...ps,
        isMandatory: promoCodeOrVoucher.productAndServiceIds.includes(parseInt(ps.id)),
      }));
    }

    return productsAndServices;
  };

  const extraProps = {
    openSession,
    sessionLoading,
    productsAndServices: formatProductsAnsServices(productsAndServices),
  };

  const { basketIsEmpty, purchase, loading: purchaseLoading, refetch: purchaseRefetch } = useContext(PurchaseContext);

  useEffect(() => {
    if (!purchaseLoading && params.registrationId && purchase && basketIsEmpty) {
      history.replace({
        pathname: generatePath(routes[0].path, params),
        state: { prevSearch: search },
      });
    }
  }, [basketIsEmpty]);

  if (
    sessionId &&
    !sessionLoading &&
    (!openSession || (openSession.testType.hideFromPublicLists && (!promoCodeOrVoucher?.isVoucher && !promoCodeOrVoucher.referrer)))
  ) {
    newNotification({
      content: t('session-registration.session.not-found'),
      type: 'warning',
    });

    return <LocalizedRedirect to={paths.SESSION_SEARCH} />;
  }

  if (registrationId && !purchaseLoading && !purchase) {
    return errorDisplay('session-registration.invalid-purchase');
  }

  if (!sessionId) {
    return renderRoutes(routes, { purchase, purchaseRefetch, purchaseLoading, registrationId });
  }

  return renderRoutes(routes, {
    ...extraProps,
    purchaseRefetch,
    purchase,
    purchaseLoading,
    registrationId,
  });
};

export default BuyingProcessLayout;
